import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppLink, Button, ErrorText, Flex, Page, PageHeader, SearchField, Table, Text } from '@cmaas/ui';
import type { Tenant } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';
import { usePage } from '../../api/usePage';
import { useSearchQuery } from '../../hooks/useSearchQuery';
import { NewTenantModal } from './NewTenantModal';

export function TenantsPage() {
  const queryClient = useQueryClient();
  const { input, setInput, q } = useSearchQuery();
  const key = ['tenants', q ?? ''];
  const page = usePage<Tenant>(key, '/v1/admin/tenants', { q });
  const [modalOpen, setModalOpen] = useState(false);

  const toggleStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      apiFetch(`/v1/admin/tenants/${id}/actions/${status === 'active' ? 'disable' : 'enable'}`, {
        method: 'POST',
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const tenants = page.data?.pages.flatMap((p) => p.data) ?? [];
  const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

  return (
    <Page>
      <PageHeader
        title="Tenants"
        actions={
          <Flex gap="3" align="center">
            <SearchField value={input} onChange={setInput} placeholder="Search by name…" />
            <Button onClick={() => setModalOpen(true)}>+ Add tenant</Button>
          </Flex>
        }
      />

      <NewTenantModal open={modalOpen} onOpenChange={setModalOpen} />

      {page.isLoading ? (
        <Text as="p">Loading…</Text>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tenants.map((t) => (
              <Table.Row key={t.id}>
                <Table.Cell>
                  <AppLink to={`/tenants/${t.id}`}>{t.name}</AppLink>
                </Table.Cell>
                <Table.Cell>{t.status}</Table.Cell>
                <Table.Cell>{new Date(t.created_at).toLocaleString()}</Table.Cell>
                <Table.Cell>
                  <Button
                    type="button"
                    variant="soft"
                    size="1"
                    onClick={() => toggleStatus.mutate({ id: t.id, status: t.status })}
                    disabled={toggleStatus.isPending}
                  >
                    {t.status === 'active' ? 'Disable' : 'Enable'}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      {page.hasNextPage && (
        <Button
          type="button"
          variant="soft"
          onClick={() => void page.fetchNextPage()}
          disabled={page.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
      {toggleStatus.isError && <ErrorText>{mutationError(toggleStatus.error)}</ErrorText>}
    </Page>
  );
}
